import { PaymentMethodType } from "../payments/paymentTypes"

export enum AuthenticationAction {
    REQUIRES_PASSWORD = "REQUIRES_PASSWORD",
    AUTHENTICATION_COMPLETE = "AUTHENTICATION_COMPLETE",
    REQUIRES_ACTIVATION_CODE = "REQUIRES_ACTIVATION_CODE",
    CREATE_PASSWORD = "CREATE_PASSWORD",
    APPROVE_ACCOUNT_FROM_URL = "APPROVE_ACCOUNT_FROM_URL",
    REQUIRES_SELECT_COMPANY = "REQUIRES_SELECT_COMPANY",
}

export enum AuthenticationTokenType {
    ACTIVATION_CODE = "ACTIVATION_CODE",
    ONE_TIME_TOKEN = "ONE_TIME_TOKEN",
    DEVICE_SECRET = "DEVICE_SECRET",
    IMPERSONATION = "IMPERSONATION",
}

export function toAuthenticationTokenType(value: string | null): AuthenticationTokenType | undefined {
    return Object.values(AuthenticationTokenType).find((tokenType) => tokenType === value)
}

export enum UserType {
    SYSTEM_OWNER = "SYSTEM_OWNER",
    SUPPORT = "SUPPORT",
    MERCHANT = "MERCHANT",
    COMPANY = "COMPANY",
    DEBTOR = "DEBTOR",
    USER = "USER",
    SERVICE_CENTER = "SERVICE_CENTER",
    SATELLITE_CANTEEN = "SATELLITE_CANTEEN",
    SUPPLIER = "SUPPLIER",
    OFFICE_MANAGER = "OFFICE_MANAGER",
    COMPANY_ADMIN = "COMPANY_ADMIN",
}

const ADMIN_USER_TYPES: ReadonlyArray<UserType> = [
    UserType.SUPPORT,
    UserType.SUPPLIER,
    UserType.MERCHANT,
    UserType.OFFICE_MANAGER,
    UserType.COMPANY,
    UserType.SERVICE_CENTER,
]

export function isAdminUserType(userType: UserType | undefined) {
    return userType !== undefined && ADMIN_USER_TYPES.includes(userType)
}

export enum AccountChangeType {
    EMAIL = "EMAIL",
}

export type CompanyChoice = Readonly<{
    uid: string
    name: string
    address?: {
        streetName: string
        streetNumber?: string
    }
    location?: {
        id: number
        name?: string
        address?: {
            streetName: string
            streetNumber?: string
        }
    }
    /** Sent next to `user` on some accounts; see the note on the nested one in `user`. */
    userGroup?: {
        id: number
    }
    user?: {
        id: number
        username?: string
        displayName?: string
        role?: UserType
        userGroup?: {
            id: number
        }
    }
}>

export enum SelectCompanyReason {
    MULTIPLE_COMPANIES_FOR_DOMAIN = "MULTIPLE_COMPANIES_FOR_DOMAIN",
    MULTIPLE_ACCOUNTS_FOR_USERNAME = "MULTIPLE_ACCOUNTS_FOR_USERNAME",
}

export type AuthenticationUser = Readonly<{
    userType: UserType
    id: number
    companyId: number
    userGroupId?: number
    serviceCenterId?: number
    username: string
    displayInitials: string
    /** Frontend-only marker of a DEVICE_SECRET session; its id is a device id, not a user id. */
    isDeviceSession?: boolean
    /** Device sessions only: the merchant uid from authenticate-by-type, for uid-addressed endpoints. */
    merchantUid?: string
}>

export type RequiresPasswordResult = Readonly<{
    type: string
    action: AuthenticationAction.REQUIRES_PASSWORD
    message?: string
}>

export type AuthenticationCompleteResult = Readonly<{
    type: string
    action: AuthenticationAction.AUTHENTICATION_COMPLETE
    message?: string
}>

export type RequiresActivationCodeResult = Readonly<{
    type: string
    action: AuthenticationAction.REQUIRES_ACTIVATION_CODE
    message?: string
}>

export type CreatePasswordResult = Readonly<{
    type: string
    action: AuthenticationAction.CREATE_PASSWORD
    message?: string
}>

export type RequiresSelectCompanyResult = Readonly<{
    type: string
    action: AuthenticationAction.REQUIRES_SELECT_COMPANY
    message?: string
}>

export type Authentication = Readonly<{
    type: string
    token: string
}>

export type RequiresPasswordResponse = Readonly<{
    authentication: Authentication
    authenticationResult: RequiresPasswordResult
}>

export type RequiresActivationCodeResponse = Readonly<{
    authentication: Authentication
    authenticationResult: RequiresActivationCodeResult
}>

export type RequiresCreatePasswordResponse = Readonly<{
    authentication: Authentication
    authenticationResult: CreatePasswordResult
    user: AuthenticationUser
}>

export type RequiresSelectCompanyResponse = Readonly<{
    authenticationResult: RequiresSelectCompanyResult
    selectCompany: {
        companies: CompanyChoice[]
        reason: SelectCompanyReason
        duplicateValue: string
    }
}>

export function isRequiresPasswordResponse(
    response: AuthenticationByTypeResponse,
): response is RequiresPasswordResponse {
    return (
        "authenticationResult" in response &&
        response.authenticationResult.action === AuthenticationAction.REQUIRES_PASSWORD
    )
}

export function isRequiresActivationCodeResponse(
    response: AuthenticationByTypeResponse,
): response is RequiresPasswordResponse {
    return (
        "authenticationResult" in response &&
        response.authenticationResult.action === AuthenticationAction.REQUIRES_ACTIVATION_CODE
    )
}

export function isRequiresCompanySelectResponse(
    response: AuthenticationByTypeResponse,
): response is RequiresPasswordResponse {
    return (
        "authenticationResult" in response &&
        response.authenticationResult.action === AuthenticationAction.REQUIRES_SELECT_COMPANY
    )
}

export function isRequiresCreatePasswordResponse(
    response: AuthenticationByTypeResponse,
): response is RequiresCreatePasswordResponse {
    return (
        "authenticationResult" in response &&
        response.authenticationResult.action === AuthenticationAction.CREATE_PASSWORD
    )
}

export type AuthenticationCompleteResponse = Readonly<{
    authenticationResult: AuthenticationCompleteResult
    authentication: Authentication
    user: AuthenticationUser
}>

export type ResetPasswordCompleteResponse = Readonly<{
    authenticationResult: AuthenticationCompleteResult
    authentication: Authentication
    user: AuthenticationUser
}>

export function isAuthenticationComplete(
    response: AuthenticationByTypeResponse,
): response is AuthenticationCompleteResponse {
    return (
        "authenticationResult" in response &&
        response.authenticationResult.action === AuthenticationAction.AUTHENTICATION_COMPLETE
    )
}

export function isCreatePassword(response: RequiresCreatePasswordResponse): response is RequiresCreatePasswordResponse {
    return response.authenticationResult.action === AuthenticationAction.CREATE_PASSWORD
}

export function isRequiresSelectCompany(
    response: AuthenticationByTypeResponse,
): response is RequiresSelectCompanyResponse {
    return (
        "authenticationResult" in response &&
        response.authenticationResult.action === AuthenticationAction.REQUIRES_SELECT_COMPANY
    )
}

export type AuthenticationResponse =
    | RequiresPasswordResponse
    | AuthenticationCompleteResponse
    | RequiresActivationCodeResponse
    | RequiresCreatePasswordResponse
    | RequiresSelectCompanyResponse

export type DeviceAuthentication = Readonly<{
    type: string
    token: string
    merchant: Readonly<{
        id: number
        uid: string
        name: string
    }>
    device: Readonly<{
        id: number
        isActive: boolean
        demoMode: boolean
        companyId: number
    }>
}>

/** The byType response for a DEVICE_SECRET login: a device session with no user attached. */
export type DeviceAuthenticationResponse = Readonly<{
    authentication: DeviceAuthentication
}>

export type AuthenticationByTypeResponse = AuthenticationResponse | DeviceAuthenticationResponse

export function isDeviceAuthentication(
    response: AuthenticationByTypeResponse,
): response is DeviceAuthenticationResponse {
    return !("authenticationResult" in response)
}

/** The stand-in user of a device session, so the app context works without a real user. */
export function toDeviceUser(authentication: DeviceAuthentication): AuthenticationUser {
    return {
        userType: UserType.MERCHANT,
        id: authentication.device.id,
        companyId: authentication.merchant.id,
        username: authentication.merchant.name,
        displayInitials: "",
        isDeviceSession: true,
        merchantUid: authentication.merchant.uid,
    }
}

type ValidatePasswordSupplierRequest = Readonly<{
    userType: UserType.SUPPLIER
    supplierDetails: {
        companyId: number
    }
    password: string
}>
type ValidatePasswordMerchantRequest = Readonly<{
    userType: UserType.MERCHANT
    merchantDetails: {
        companyId: number
    }
    password: string
}>
type ValidatePasswordServiceCenterRequest = Readonly<{
    userType: UserType.SERVICE_CENTER
    serviceCenterDetails: {
        serviceCenterId: number
        companyId: number
    }
    password: string
}>
type ValidatePasswordCompanyRequest = Readonly<{
    userType: UserType.COMPANY
    companyDetails: {
        companyId: number
    }
    password: string
}>
type ValidatePasswordTenantRequest = Readonly<{
    userType: UserType.OFFICE_MANAGER
    officeManagerDetails: {
        companyId: number
        userGroupId: number
    }
    password: string
}>

type ValidatePasswordUserRequest = Readonly<{
    userType: UserType.USER
    companyUserDetails: {
        companyId: number
        userGroupId: number
    }
    password: string
}>

export type ValidatePasswordRequest =
    | ValidatePasswordSupplierRequest
    | ValidatePasswordMerchantRequest
    | ValidatePasswordServiceCenterRequest
    | ValidatePasswordCompanyRequest
    | ValidatePasswordTenantRequest
    | ValidatePasswordUserRequest

type ValidateSuccessResponse = Readonly<{
    validator: {
        success: true
    }
}>

type ValidateErrorResponse = Readonly<{
    validator: {
        success: false
        rules: string[]
    }
}>

export type ValidatePasswordResponse = ValidateSuccessResponse | ValidateErrorResponse

export enum LoginStatus {
    AUTHENTICATING = "AUTHENTICATING",
    LOGGED_IN = "LOGGED_IN",
}

type AuthenticatingState = Readonly<{
    status: LoginStatus.AUTHENTICATING
    username: string | undefined
    rememberMe: boolean
    response?: AuthenticationResponse
}>

type LoggedInState = Readonly<{
    status: LoginStatus.LOGGED_IN
    user: AuthenticationUser
    authToken: string
}>

export type LoginState = AuthenticatingState | LoggedInState

// ID CARD
export type ActivateIdCardBody = Readonly<{
    user: ActivateIdCardNewUser | ActivateIdCardExistUser
    cardSubscription: {
        activationToken: string
        subscriptionId: number
    }
    authentication: Authentication
}>
type ActivateIdCardNewUser = Readonly<{
    displayName: string
    mobileNumber?: string
    userGroup: {
        id: number
    }
    company: {
        id: number
    }
}>

type ActivateIdCardExistUser = Readonly<{
    uid: string
}>

export type IdCardSubscription = Readonly<{
    id: number
    name: string
    description: string
    subscriptionType: string
    paymentMethod: PaymentMethodType
}>
export type CardSubscriptionCompaniesResponse = Readonly<{
    companies: IdCardSubscriptionCompany[]
    user: IdCardSubscriptionUser
}>

export type IdCardSubscriptionUser = Readonly<{
    email: string
    displayName?: string
    uid: string
}>

export type SignUpSettings = Readonly<{
    emailDomainMessage?: string
    acceptedEmailDomains?: string[]
}>

export type IdCardSubscriptionCompanyUserGroup = Readonly<{
    id: number
    name: string
    displayName?: string
    signUpSettings?: SignUpSettings
}>

export type IdCardSubscriptionDepartment = Readonly<{
    id: number
    name: string
}>

export type IdCardSubscriptionCompany = Readonly<
    | {
          id: number
          name: string
          isOfficeHotel?: boolean
          departments?: IdCardSubscriptionDepartment[]
          userGroups: IdCardSubscriptionCompanyUserGroup[]
      }
    | {
          id: number
          name: string
          userGroup: IdCardSubscriptionCompanyUserGroup
          userGroups?: never
      }
>

export type IdCardSignupFormValues = Readonly<{
    displayName: string
    subscriptionId: number | null
    mobileNumber: string
    companyId: string | number | null
    userGroupId: string | null
}>

export type TransformedCompany = Readonly<
    | {
          id: string
          displayName: string
          isGroup: false
          companyId: number
          userGroup?: IdCardSubscriptionCompanyUserGroup
          userGroups: IdCardSubscriptionCompanyUserGroup[]
          departments: ReadonlyArray<IdCardSubscriptionDepartment>
      }
    | {
          id: string
          displayName: string
          isGroup: true
          companyId: number
          userGroupId: number
          signUpSettings?: SignUpSettings
      }
>

export type CompanyIdChoice = Readonly<{
    uid: string
    location?: {
        id: number
    }
    userGroup?: {
        id: number
    }
    user: {
        username: string
    }
}>
